// src/pages/SuperAdmin/PaymentsManager.jsx
import React, { useState } from 'react';
import { Row, Col, Table, Button, Input, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Badge } from 'reactstrap';
import { post } from 'helpers/api_helper';

const PaymentsManager = ({ payments, tenants, loading, onRefresh }) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Record Payment Modal
  const [isRecordModalOpen, setIsRecordModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    clinic_id: '',
    amount: '',
    tax_amount: 0,
    payment_method: 'bank_transfer',
    transaction_id: '',
    payment_status: 'paid',
    notes: '',
  });

  const filteredPayments = payments.filter((p) => {
    const matchSearch =
      (p.invoice_number || '').toLowerCase().includes(search.toLowerCase()) ||
      (p.clinic?.clinic_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (p.transaction_id || '').toLowerCase().includes(search.toLowerCase());

    if (statusFilter === 'paid') return matchSearch && p.payment_status === 'paid';
    if (statusFilter === 'pending') return matchSearch && p.payment_status === 'pending';
    if (statusFilter === 'failed') return matchSearch && p.payment_status === 'failed';

    return matchSearch;
  });

  const totalCollected = filteredPayments
    .filter((p) => p.payment_status === 'paid')
    .reduce((acc, curr) => acc + Number(curr.total_amount || 0), 0);

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await post('/superadmin/payments', formData);
      setIsRecordModalOpen(false);
      setFormData({
        clinic_id: '',
        amount: '',
        tax_amount: 0,
        payment_method: 'bank_transfer',
        transaction_id: '',
        payment_status: 'paid',
        notes: '',
      });
      onRefresh();
    } catch (err) {
      alert(err?.message || 'Error recording payment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="sa-card">
        <div className="sa-card-header">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <h5 className="sa-card-title">
              <i className="bx bx-money text-success" /> Payments & Invoices Ledger
            </h5>
            <div className="d-flex align-items-center gap-2">
              <Input
                type="text"
                className="form-control-sm"
                placeholder="Search invoice, clinic, UTR..."
                style={{ width: 260 }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Input
                type="select"
                className="form-select-sm"
                style={{ width: 140 }}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Payments</option>
                <option value="paid">Paid (Success)</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed / Cancelled</option>
              </Input>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <div className="bg-light px-3 py-1 rounded border font-size-12">
              <span className="text-muted">Total Filtered:</span>{' '}
              <strong className="text-success">₹{totalCollected.toLocaleString()}</strong>
            </div>

            <button
              type="button"
              className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1 font-weight-semibold shadow-sm"
              onClick={() => setIsRecordModalOpen(true)}
            >
              <i className="bx bx-plus-circle font-size-14" /> Record Offline Payment
            </button>
          </div>
        </div>

        <div className="sa-card-body p-0">
          <div className="table-responsive">
            <Table className="table align-middle table-hover table-nowrap mb-0 font-size-12">
              <thead className="table-light">
                <tr>
                  <th>Invoice #</th>
                  <th>Clinic / Tenant</th>
                  <th>Amount</th>
                  <th>Tax</th>
                  <th>Total Paid</th>
                  <th>Method & UTR</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-5 text-muted">
                      {loading ? 'Loading payment records...' : 'No payments found matching the filters.'}
                    </td>
                  </tr>
                ) : (
                  filteredPayments.map((p) => {
                    const isPaid = p.payment_status === 'paid';
                    const isPending = p.payment_status === 'pending';

                    return (
                      <tr key={p.id}>
                        <td>
                          <span className="font-monospace font-weight-bold text-dark">{p.invoice_number}</span>
                        </td>

                        <td>
                          <div className="font-weight-semibold text-dark">{p.clinic?.clinic_name || 'Clinic'}</div>
                          <small className="text-muted">{p.client?.email || '-'}</small>
                        </td>

                        <td>₹{Number(p.amount || 0).toLocaleString()}</td>
                        <td>₹{Number(p.tax_amount || 0).toLocaleString()}</td>

                        <td>
                          <div className="font-weight-bold text-success font-size-13">
                            ₹{Number(p.total_amount || 0).toLocaleString()}
                          </div>
                        </td>

                        <td>
                          <span className="badge bg-light text-dark text-capitalize border">
                            {p.payment_method || 'manual'}
                          </span>
                          {p.transaction_id && (
                            <div className="font-size-10 text-muted font-monospace">{p.transaction_id}</div>
                          )}
                        </td>

                        <td>
                          <span
                            className={`badge ${
                              isPaid ? 'bg-success-subtle text-success border border-success' : isPending ? 'bg-warning-subtle text-warning border border-warning' : 'bg-danger-subtle text-danger'
                            }`}
                          >
                            {p.payment_status?.toUpperCase()}
                          </span>
                        </td>

                        <td>{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : '-'}</td>

                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-xs btn-outline-dark"
                            onClick={() => {
                              setSelectedInvoice(p);
                              setIsInvoiceModalOpen(true);
                            }}
                          >
                            <i className="bx bx-receipt me-1" /> View Receipt
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      {/* Record Offline Payment Modal */}
      <Modal isOpen={isRecordModalOpen} toggle={() => setIsRecordModalOpen(!isRecordModalOpen)} centered>
        <ModalHeader toggle={() => setIsRecordModalOpen(!isRecordModalOpen)}>
          <i className="bx bx-plus-circle text-primary me-2" /> Record Manual / Offline Payment
        </ModalHeader>
        <Form onSubmit={handleRecordPayment}>
          <ModalBody>
            <FormGroup className="mb-3">
              <Label className="font-weight-semibold font-size-12">Select Dental Clinic *</Label>
              <Input
                type="select"
                required
                value={formData.clinic_id}
                onChange={(e) => setFormData({ ...formData, clinic_id: e.target.value })}
              >
                <option value="">-- Choose Clinic --</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.clinic_name} ({t.city || 'No city'})
                  </option>
                ))}
              </Input>
            </FormGroup>

            <Row>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">Net Amount (₹) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    required
                    placeholder="e.g. 2999"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">GST / Tax Amount (₹)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.tax_amount}
                    onChange={(e) => setFormData({ ...formData, tax_amount: e.target.value })}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">Payment Mode</Label>
                  <Input
                    type="select"
                    value={formData.payment_method}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  >
                    <option value="bank_transfer">Bank Transfer (NEFT/RTGS/IMPS)</option>
                    <option value="upi">UPI / QR Code</option>
                    <option value="cash">Cash Receipt</option>
                    <option value="cheque">Cheque</option>
                    <option value="razorpay">Razorpay Gateway</option>
                    <option value="stripe">Stripe Gateway</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">Transaction Ref / UTR</Label>
                  <Input
                    type="text"
                    placeholder="e.g. UTR-982347102"
                    value={formData.transaction_id}
                    onChange={(e) => setFormData({ ...formData, transaction_id: e.target.value })}
                  />
                </FormGroup>
              </Col>
            </Row>

            <FormGroup className="mb-2">
              <Label className="font-weight-semibold font-size-12">Payment Notes</Label>
              <Input
                type="textarea"
                rows={2}
                placeholder="Optional notes or cheque number"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setIsRecordModalOpen(false)}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? 'Recording...' : 'Save & Generate Invoice'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Invoice Receipt Modal */}
      <Modal isOpen={isInvoiceModalOpen} toggle={() => setIsInvoiceModalOpen(!isInvoiceModalOpen)} centered>
        <ModalHeader toggle={() => setIsInvoiceModalOpen(!isInvoiceModalOpen)}>
          <i className="bx bx-receipt text-warning me-2" /> Subscription Invoice Receipt
        </ModalHeader>
        <ModalBody>
          {selectedInvoice && (
            <div className="p-3 border rounded">
              <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                <div>
                  <h6 className="mb-0 font-weight-bold text-dark">ORALSTOP PLATFORM</h6>
                  <small className="text-muted">Multi-Tenant Dental OPD</small>
                </div>
                <div className="text-end">
                  <Badge color="success">PAID</Badge>
                  <div className="font-size-11 font-monospace mt-1">{selectedInvoice.invoice_number}</div>
                </div>
              </div>

              <div className="mb-3 font-size-12">
                <div><strong>Billed To:</strong> {selectedInvoice.clinic?.clinic_name}</div>
                <div><strong>Email:</strong> {selectedInvoice.client?.email}</div>
                <div><strong>Date:</strong> {new Date(selectedInvoice.paid_at).toLocaleString()}</div>
                <div><strong>Payment Mode:</strong> {selectedInvoice.payment_method?.toUpperCase()}</div>
                {selectedInvoice.transaction_id && <div><strong>Ref / UTR:</strong> {selectedInvoice.transaction_id}</div>}
              </div>

              <Table size="sm" className="mb-3 font-size-12">
                <thead>
                  <tr className="table-light">
                    <th>Description</th>
                    <th className="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Platform Software Subscription ({selectedInvoice.subscription?.plan?.name || 'Standard Tier'})</td>
                    <td className="text-end">₹{Number(selectedInvoice.amount).toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td>GST / Taxes</td>
                    <td className="text-end">₹{Number(selectedInvoice.tax_amount).toLocaleString()}</td>
                  </tr>
                  <tr className="font-weight-bold border-top">
                    <td>Total Paid</td>
                    <td className="text-end text-success">₹{Number(selectedInvoice.total_amount).toLocaleString()}</td>
                  </tr>
                </tbody>
              </Table>

              {selectedInvoice.notes && (
                <div className="font-size-11 text-muted p-2 bg-light rounded">
                  <strong>Notes:</strong> {selectedInvoice.notes}
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setIsInvoiceModalOpen(false)}>
            Close
          </Button>
          <Button color="primary" onClick={() => window.print()}>
            <i className="bx bx-printer me-1" /> Print Receipt
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PaymentsManager;
